/**
 * Utility functions for managing optional columns across list views
 */

/**
 * Filters global optional columns to only those that exist in the current table
 * @param {Array} allColumns - All columns defined for the table
 * @param {Array} globalOptionalColumns - Optional columns from global config
 * @returns {Array} Filtered array of column IDs that exist in both lists
 */
export const getTableSpecificOptionalColumns = (allColumns, globalOptionalColumns = []) => {
  const availableColumnIds = new Set(allColumns.map(col => col.property));
  return globalOptionalColumns.filter(colId => availableColumnIds.has(colId));
};

/**
 * Creates a mapping of column IDs to their display names
 * @param {Array} allColumns - All columns defined for the table
 * @returns {Object} Map of column property to column name
 */
export const createColumnNameMapping = (allColumns) => {
  const mapping = {};
  allColumns.forEach(col => {
    mapping[col.property] = col.name;
  });
  return mapping;
};

/**
 * Filters columns based on optional columns configuration and user preferences
 * @param {Array} allColumns - All columns defined for the table
 * @param {Set} visibleOptionalColumns - Set of optional column IDs the user wants visible
 * @param {boolean} isOptionalColumnsEnabled - Whether the feature is enabled
 * @param {Array} globalOptionalColumns - Optional columns from global config
 * @returns {Array} Filtered array of columns to display
 */
export const filterColumns = (
  allColumns,
  visibleOptionalColumns,
  isOptionalColumnsEnabled,
  globalOptionalColumns = []
) => {
  return allColumns.filter((col) => {
    if (!isOptionalColumnsEnabled) {
      // When feature is OFF, hide columns in the global optional list
      return !globalOptionalColumns.includes(col.property);
    }
    // When feature is ON, show if it's not optional OR if user has toggled it visible
    const isOptional = globalOptionalColumns.includes(col.property);
    return !isOptional || visibleOptionalColumns.has(col.property);
  });
};

/**
 * Main helper function to build columns with optional columns logic
 * @param {Array} allColumns - All columns defined for the table
 * @param {Object} listViewState - The list view state instance
 * @param {Object} rootStore - Root store containing app configs
 * @returns {Array} Filtered columns ready to be set on the table
 */
export const buildColumnsWithOptionalSupport = (
  allColumns,
  listViewState,
  rootStore
) => {
  const globalOptionalColumns = rootStore?.appConfigs?.configs?.optional_columns || [];
  const isOptionalColumnsEnabled = rootStore?.appConfigs?.configs?.optional_columns_enabled;

  // Filter to table-specific optional columns
  const tableSpecificOptionalColumns = getTableSpecificOptionalColumns(
    allColumns,
    globalOptionalColumns
  );

  // Store for dropdown to use
  listViewState.setTableOptionalColumns(tableSpecificOptionalColumns);

  // Create name mapping for labels
  const nameMapping = createColumnNameMapping(allColumns);
  listViewState.setColumnNameMapping(nameMapping);

  // Get visible optional columns from state
  const visibleOptionalColumns = new Set(listViewState.optionalColumns);

  // Filter columns
  const filteredColumns = filterColumns(
    allColumns,
    visibleOptionalColumns,
    isOptionalColumnsEnabled,
    globalOptionalColumns
  );

  return filteredColumns;
};
